"use client"

import { useEffect, useRef, useState } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
      reset: (id?: string) => void
    }
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

/**
 * True when a Turnstile site key is configured for this build. When false the
 * widget renders nothing and the client MUST NOT block submits on a captcha
 * token (there is none) — the server skips captcha too in that case. See
 * `useSpamGuard`'s `ready`/`captchaRequired`.
 */
export const TURNSTILE_ENABLED = Boolean(SITE_KEY)

// Load the Turnstile script once, shared across all widget instances.
let scriptPromise: Promise<void> | null = null
function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    )
    if (existing) {
      existing.addEventListener("load", () => resolve())
      existing.addEventListener("error", () => reject())
      if (window.turnstile) resolve()
      return
    }
    const s = document.createElement("script")
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject()
    document.head.appendChild(s)
  })
  return scriptPromise
}

interface TurnstileProps {
  onToken: (token: string) => void
  onExpire?: () => void
  /** Fired when the widget itself errors (e.g. domain not allowed, network). */
  onError?: () => void
  className?: string
}

/**
 * Cloudflare Turnstile widget. Renders nothing when no site key is configured
 * (e.g. local dev) — the server still enforces captcha when its secret is set.
 */
export function Turnstile({ onToken, onExpire, onError, className }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  // Hide the widget once it has issued a token; show it again if the token
  // expires or Cloudflare asks the visitor to interact.
  const [verified, setVerified] = useState(false)
  // Keep latest callbacks without re-running the effect.
  const cbRef = useRef({ onToken, onExpire, onError })
  cbRef.current = { onToken, onExpire, onError }

  useEffect(() => {
    if (!SITE_KEY) return
    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        if (widgetIdRef.current) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          // Only show the widget when Cloudflare needs the visitor to click;
          // an automatic pass stays invisible instead of showing "Success!".
          appearance: "interaction-only",
          callback: (token: string) => {
            cbRef.current.onToken(token)
            // Leave the success tick visible for a moment before hiding it.
            setTimeout(() => setVerified(true), 1500)
          },
          "before-interactive-callback": () => setVerified(false),
          "expired-callback": () => {
            setVerified(false)
            cbRef.current.onExpire?.()
          },
          // Distinct from expiry: the widget failed to issue a token at all
          // (common cause: this hostname isn't in the widget's allowed domains
          // in the Cloudflare dashboard → error 110200). Surface it so the form
          // can tell the user instead of silently disabling submit forever.
          "error-callback": () => {
            setVerified(false)
            cbRef.current.onExpire?.()
            cbRef.current.onError?.()
          },
        })
      })
      .catch(() => {
        /* script/network failure — server-side captcha check still applies */
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null
      }
    }
  }, [])

  if (!SITE_KEY) return null
  return (
    <div
      ref={containerRef}
      className={className}
      style={verified ? { display: "none" } : undefined}
    />
  )
}
